import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { create } from '@bufbuild/protobuf';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
  Button,
  Stack,
  TextField,
  Checkbox,
  IconButton,
  Chip,
  Divider,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useLocation, useNavigate } from 'react-router-dom';
import type { UploadReceiptResponse, ReceiptSummary } from '../../generated/receipt/v1/receipt_pb';
import { listReceipts, getReceipt } from '../api/receipt';
import { ShoppingListItemInputSchema } from '../../generated/shopping/v1/shopping_pb';
import type { ShoppingList, ShoppingListSummary, ShoppingListItem } from '../../generated/shopping/v1/shopping_pb';
import {
  addShoppingListItem,
  checkShoppingListItem,
  createShoppingList,
  deleteShoppingList,
  getShoppingList,
  listShoppingLists,
  removeShoppingListItem,
  unpairReceiptFromShoppingList,
  updateShoppingList,
  updateShoppingListItem,
  ShoppingListSortField,
  ShoppingListSortOrder,
  ShoppingListStatus,
} from '../api/shoppingList';
import { ErrorAlert } from '../components/ErrorAlert';
import { LoadingBackdrop } from '../components/LoadingBackdrop';
import { useUploadStore } from '../store/uploadStore';
import { useDescriptionMappingStore } from '../store/descriptionMappingStore';
import { QuantityInput } from '../components/QuantityInput';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CategoryIcon from '@mui/icons-material/Category';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SpaIcon from '@mui/icons-material/Spa';
import EggIcon from '@mui/icons-material/Egg';
import KebabDiningIcon from '@mui/icons-material/KebabDining';
import BakeryDiningIcon from '@mui/icons-material/BakeryDining';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import KitchenIcon from '@mui/icons-material/Kitchen';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import CookieIcon from '@mui/icons-material/Cookie';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import Inventory2Icon from '@mui/icons-material/Inventory2';

// Default item categories
const DEFAULT_CATEGORIES = [
  'Produce',
  'Dairy',
  'Meat & Seafood',
  'Bakery',
  'Frozen',
  'Pantry',
  'Beverages',
  'Snacks',
  'Household',
  'Other',
] as const;

const UNCATEGORIZED = 'Uncategorized';

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Produce':
      return <SpaIcon fontSize="small" color="primary" />;
    case 'Dairy':
      return <EggIcon fontSize="small" color="primary" />;
    case 'Meat & Seafood':
      return <KebabDiningIcon fontSize="small" color="primary" />;
    case 'Bakery':
      return <BakeryDiningIcon fontSize="small" color="primary" />;
    case 'Frozen':
      return <AcUnitIcon fontSize="small" color="primary" />;
    case 'Pantry':
      return <KitchenIcon fontSize="small" color="primary" />;
    case 'Beverages':
      return <LocalDrinkIcon fontSize="small" color="primary" />;
    case 'Snacks':
      return <CookieIcon fontSize="small" color="primary" />;
    case 'Household':
      return <CleaningServicesIcon fontSize="small" color="primary" />;
    case 'Other':
      return <Inventory2Icon fontSize="small" color="primary" />;
    default:
      return <CategoryIcon fontSize="small" color="primary" />;
  }
};

interface LocationState {
  fromReceipt?: UploadReceiptResponse;
  fromReceiptSummary?: ReceiptSummary;
  viewListId?: string;
  isArchived?: boolean;
}

interface SortableRowProps {
  id: string;
  children: React.ReactNode;
}

function SortableRow({ id, children }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      <TableCell sx={{ width: 32, px: 0.5 }}>
        <Box
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      </TableCell>
      {children}
    </TableRow>
  );
}

// Mobile sortable card component
function SortableCard({ id, children }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper ref={setNodeRef} style={style} {...attributes} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
            mt: 0.5,
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
    </Paper>
  );
}

interface DroppableCategoryProps {
  category: string;
  children: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  itemCount: number;
  isOver?: boolean;
}

function DroppableCategory({ category, children, isExpanded, onToggle, itemCount, isOver }: DroppableCategoryProps) {
  const { setNodeRef } = useDroppable({ id: `category-${category}` });
  const theme = useTheme();

  return (
    <Box ref={setNodeRef} sx={{ mb: 2 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1,
          px: 1.5,
          bgcolor: isOver ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.08),
          borderRadius: 1,
          cursor: 'pointer',
          border: isOver ? `2px dashed ${theme.palette.primary.main}` : '2px solid transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {getCategoryIcon(category)}
          <Typography variant="subtitle2" fontWeight={600}>
            {category}
          </Typography>
          <Chip label={itemCount} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
        </Stack>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      {isExpanded && (
        <Box sx={{ mt: 1 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

export default function ShoppingListsPage() {
  const theme = useTheme();
  const isDesktopViewport = useMediaQuery(theme.breakpoints.up('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;
  const pendingUploads = useUploadStore((state) => state.uploads);
  const getUploadForList = (listId: string) => {
    for (const upload of pendingUploads.values()) {
      if (upload.pairWithListId === listId && (upload.status === 'pending' || upload.status === 'uploading')) {
        return upload;
      }
    }
    return undefined;
  };

  const receipt = state?.fromReceipt;
  const receiptSummary = state?.fromReceiptSummary;
  const viewListId = state?.viewListId;
  const isArchivedView = state?.isArchived ?? false;

  const [lists, setLists] = useState<ShoppingListSummary[]>([]);
  const [viewingCompletedList, setViewingCompletedList] = useState(false);
  const [selectedList, setSelectedList] = useState<ShoppingList | null>(null);
  const [listName, setListName] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState(1);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editItemDescription, setEditItemDescription] = useState('');
  const [editItemQuantity, setEditItemQuantity] = useState(1);
  const [editItemCategory, setEditItemCategory] = useState('');
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editListName, setEditListName] = useState('');
  const [editListDate, setEditListDate] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListStore, setNewListStore] = useState('');
  const [receiptStores, setReceiptStores] = useState<string[]>([]);
  const itemDescriptionOptions = useDescriptionMappingStore((state) => state.options);
  const [itemOrder, setItemOrder] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [newItemCategory, setNewItemCategory] = useState<string>('');
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<string | null>(null);
  const createListFromReceiptIdRef = useRef<string | null>(null);

  const isReceiptFlow = !!receipt;
  const isSummaryFlow = !!receiptSummary;

  // Set up dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const listDisplayName = useMemo(() => {
    if (!selectedList) {
      return '';
    }
    return selectedList.name || 'Untitled List';
  }, [selectedList]);

  // Initialize item order when selectedList changes
  useEffect(() => {
    if (selectedList) {
      // Initialize order based on current items, preserving any existing custom order
      const currentIds = selectedList.items.map((item) => item.itemId);
      const validOrder = itemOrder.filter((id) => currentIds.includes(id));
      const newIds = currentIds.filter((id) => !validOrder.includes(id));
      if (newIds.length > 0 || validOrder.length !== itemOrder.length) {
        setItemOrder([...validOrder, ...newIds]);
      }
    } else {
      setItemOrder([]);
    }
  }, [selectedList?.listId, selectedList?.items.length]);

  const sortedSelectedItems = useMemo(() => {
    if (!selectedList) {
      return [];
    }

    // If we have a custom order, use it; otherwise fall back to alphabetical
    if (itemOrder.length > 0) {
      const itemMap = new Map(selectedList.items.map((item) => [item.itemId, item]));
      return itemOrder
        .map((id) => itemMap.get(id))
        .filter((item): item is ShoppingListItem => item !== undefined);
    }

    return [...selectedList.items].sort((first, second) => {
      const firstLabel = (first.customDescription || first.description || '').trim();
      const secondLabel = (second.customDescription || second.description || '').trim();
      return firstLabel.localeCompare(secondLabel, undefined, { sensitivity: 'base' });
    });
  }, [selectedList, itemOrder]);

  // Group items by category
  const itemsByCategory = useMemo(() => {
    const groups: Record<string, ShoppingListItem[]> = {};

    for (const item of sortedSelectedItems) {
      const category = item.category || UNCATEGORIZED;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    }

    return groups;
  }, [sortedSelectedItems]);

  // Get all categories that have items, plus any default categories for empty drops
  const activeCategories = useMemo(() => {
    const cats = new Set(Object.keys(itemsByCategory));
    // Add Uncategorized if we have any items without category
    if (itemsByCategory[UNCATEGORIZED]?.length > 0) {
      cats.add(UNCATEGORIZED);
    }
    return Array.from(cats).sort((a, b) => {
      // Put Uncategorized at the end
      if (a === UNCATEGORIZED) return 1;
      if (b === UNCATEGORIZED) return -1;
      return a.localeCompare(b);
    });
  }, [itemsByCategory]);

  // Get the item being dragged
  const activeDragItem = useMemo(() => {
    if (!activeDragId || !selectedList) return null;
    return selectedList.items.find((item) => item.itemId === activeDragId) || null;
  }, [activeDragId, selectedList]);

  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  }, []);

  // Initialize all categories as expanded
  useEffect(() => {
    if (selectedList) {
      const allExpanded: Record<string, boolean> = {};
      for (const cat of activeCategories) {
        allExpanded[cat] = expandedCategories[cat] ?? true;
      }
      if (Object.keys(allExpanded).some((k) => expandedCategories[k] === undefined)) {
        setExpandedCategories(allExpanded);
      }
    }
  }, [activeCategories, selectedList?.listId]);

  // Auto-collapse categories when all items are checked
  useEffect(() => {
    if (!selectedList) return;

    const categoriesToCollapse: string[] = [];
    for (const [category, items] of Object.entries(itemsByCategory)) {
      if (items.length > 0 && items.every((item) => item.isChecked)) {
        // All items in this category are checked - collapse if expanded
        if (expandedCategories[category] === true) {
          categoriesToCollapse.push(category);
        }
      }
    }

    if (categoriesToCollapse.length > 0) {
      setExpandedCategories((prev) => {
        const updated = { ...prev };
        for (const cat of categoriesToCollapse) {
          updated[cat] = false;
        }
        return updated;
      });
    }
  }, [itemsByCategory, selectedList?.listId]);

  const handleDragStart = useCallback((event: { active: { id: string | number } }) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) {
      setDragOverCategory(null);
      return;
    }
    const overId = String(over.id);
    if (overId.startsWith('category-')) {
      setDragOverCategory(overId.replace('category-', ''));
    } else if (selectedList) {
      // Highlight the destination category when hovering over an item in a different category
      const activeId = String(active.id);
      const activeItem = selectedList.items.find((i) => i.itemId === activeId);
      const overItem = selectedList.items.find((i) => i.itemId === overId);
      if (activeItem && overItem) {
        const activeCategory = activeItem.category || UNCATEGORIZED;
        const overCategory = overItem.category || UNCATEGORIZED;
        if (activeCategory !== overCategory) {
          setDragOverCategory(overCategory);
          return;
        }
      }
      setDragOverCategory(null);
    } else {
      setDragOverCategory(null);
    }
  }, [selectedList]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);
    setDragOverCategory(null);

    if (!over || !selectedList) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Check if dropped on a category header
    if (overId.startsWith('category-')) {
      const newCategory = overId.replace('category-', '');
      const item = selectedList.items.find((i) => i.itemId === activeId);

      if (item && (item.category || UNCATEGORIZED) !== newCategory) {
        try {
          const response = await updateShoppingListItem({
            listId: selectedList.listId,
            itemId: activeId,
            category: newCategory === UNCATEGORIZED ? '' : newCategory,
          });

          if (response.item) {
            setSelectedList((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                items: prev.items.map((i) =>
                  i.itemId === activeId
                    ? { ...i, category: newCategory === UNCATEGORIZED ? undefined : newCategory }
                    : i
                ),
              };
            });
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to update item category.');
        }
      }
      return;
    }

    if (active.id !== over.id) {
      const activeItem = selectedList.items.find((i) => i.itemId === activeId);
      const overItem = selectedList.items.find((i) => i.itemId === overId);

      // Cross-category drop: move the active item into the target item's category
      if (activeItem && overItem) {
        const activeCategory = activeItem.category || UNCATEGORIZED;
        const overCategory = overItem.category || UNCATEGORIZED;

        if (activeCategory !== overCategory) {
          try {
            const response = await updateShoppingListItem({
              listId: selectedList.listId,
              itemId: activeId,
              category: overCategory === UNCATEGORIZED ? '' : overCategory,
            });

            if (response.item) {
              setSelectedList((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  items: prev.items.map((i) =>
                    i.itemId === activeId
                      ? { ...i, category: overCategory === UNCATEGORIZED ? undefined : overCategory }
                      : i
                  ),
                };
              });
            }
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update item category.');
          }
          return;
        }
      }

      // Regular reordering within same category
      setItemOrder((items) => {
        const oldIndex = items.indexOf(activeId);
        const newIndex = items.indexOf(overId);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, [selectedList]);

  const knownStores = useMemo(() => {
    return Array.from(new Set(receiptStores)).sort();
  }, [receiptStores]);

  useEffect(() => {
    if (isReceiptFlow) {
      return;
    }

    // If we have a receipt summary, fetch full receipt and create list
    if (isSummaryFlow && receiptSummary) {
      if (createListFromReceiptIdRef.current !== receiptSummary.receiptId) {
        createListFromReceiptIdRef.current = receiptSummary.receiptId;
        void createListFromReceiptSummary(receiptSummary.receiptId);
      }
      return;
    }

    // If we have a viewListId, load that specific list
    if (viewListId) {
      if (isArchivedView) {
        // Viewing a completed list from the Completed Lists page
        void loadCompletedList(viewListId);
      } else {
        // Viewing a newly created or regular list - load lists then expand the specific one
        void (async () => {
          await loadLists();
          await loadReceiptStores();
          await handleViewList(viewListId);
        })();
      }
      return;
    }

    void loadLists();
    void loadReceiptStores();
  }, [isReceiptFlow, isSummaryFlow, viewListId, isArchivedView]);

  // Update lists when an upload completes
  const lastCompletedUploadId = useUploadStore((state) => state.lastCompletedUploadId);
  useEffect(() => {
    if (lastCompletedUploadId && !isReceiptFlow && !isSummaryFlow && !viewListId) {
      const completedUpload = useUploadStore.getState().uploads.get(lastCompletedUploadId);
      if (completedUpload?.pairWithListId && completedUpload.receiptId) {
        // Update the list locally to show the linked receipt badge.
        // Re-fetching would remove it from view since loadLists() uses hasPairedReceipt: false.
        const pairedListId = completedUpload.pairWithListId;
        const pairedReceiptId = completedUpload.receiptId;
        setLists((prev) =>
          prev.map((list) =>
            list.listId === pairedListId ? { ...list, pairedReceiptId } as ShoppingListSummary : list
          )
        );
      } else {
        void loadLists();
      }
    }
  }, [lastCompletedUploadId, isReceiptFlow, isSummaryFlow, viewListId]);

  const loadCompletedList = async (listId: string) => {
    setLoadingMessage('Loading completed list...');
    setIsLoading(true);
    setError(null);

    try {
      const response = await getShoppingList({ listId });
      if (response.shoppingList) {
        setSelectedList(response.shoppingList);
        setViewingCompletedList(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load completed list.');
    } finally {
      setIsLoading(false);
    }
  };

  const createListFromReceiptSummary = async (receiptId: string) => {
    setLoadingMessage('Creating shopping list from receipt...');
    setIsLoading(true);
    setError(null);

    try {
      // Fetch the full receipt with items
      const fullReceipt = await getReceipt({ receiptId });
      if (!fullReceipt.receiptId) {
        throw new Error('Receipt not found');
      }

      // Create shopping list items from receipt items
      const items = fullReceipt.items.map((item) =>
        create(ShoppingListItemInputSchema, {
          description: item.description,
          customDescription: item.customDescription || undefined,
          quantity: item.quantity ?? 1,
          unitPrice: item.unitPrice,
          price: item.price,
          category: item.category || undefined,
        })
      );

      // Create the shopping list
      const response = await createShoppingList({
        name: undefined, // Let the system generate a name or leave empty
        store: fullReceipt.store || undefined,
        date: fullReceipt.date || undefined,
        items,
        sourceReceiptId: fullReceipt.receiptId || undefined,
      });

      if (response.shoppingList) {
        // Navigate to view the newly created list
        navigate('/lists', {
          state: { viewListId: response.shoppingList.listId },
          replace: true,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shopping list.');
      setIsLoading(false);
    }
  };

  const loadReceiptStores = async () => {
    try {
      const response = await listReceipts({ pageSize: 50 });
      const stores = response.receipts
        .map((r) => r.store)
        .filter((store): store is string => Boolean(store && store.trim()));
      setReceiptStores(stores);
    } catch {
      // Silently fail - store suggestions are not critical
    }
  };

  const loadLists = async () => {
    setLoadingMessage('Loading shopping lists...');
    setIsLoading(true);
    setError(null);

    try {
      // Use server-side filtering to only fetch lists without paired receipts
      const response = await listShoppingLists({
        pageSize: 50,
        page: 1,
        sortBy: ShoppingListSortField.UPDATED_AT,
        sortOrder: ShoppingListSortOrder.DESC,
        hasPairedReceipt: false,
      });
      setLists(response.shoppingLists);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shopping lists.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateList = async () => {
    if (!receipt) {
      return;
    }

    setLoadingMessage('Creating shopping list...');
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const items = receipt.items.map((item) =>
        create(ShoppingListItemInputSchema, {
          description: item.description,
          customDescription: item.customDescription || undefined,
          quantity: item.quantity ?? 1,
          unitPrice: item.unitPrice,
          price: item.price,
          category: item.category || undefined,
        })
      );

      const response = await createShoppingList({
        name: listName.trim() || undefined,
        store: receipt.store || undefined,
        date: receipt.date || undefined,
        items,
        sourceReceiptId: receipt.receiptId || undefined,
      });

      if (response.shoppingList) {
        setSelectedList(response.shoppingList);
      }
      setSuccess('Shopping list created successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shopping list.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEmptyList = async () => {
    setLoadingMessage('Creating shopping list...');
    setIsLoading(true);
    setError(null);

    try {
      const response = await createShoppingList({
        name: newListName.trim() || undefined,
        store: newListStore.trim() || undefined,
      });

      if (response.shoppingList) {
        // Clear the form and close it
        setShowCreateForm(false);
        setNewListName('');
        setNewListStore('');

        // Navigate to view the newly created list
        navigate('/lists', {
          state: { viewListId: response.shoppingList.listId },
          replace: true,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shopping list.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewList = async (listId: string) => {
    setLoadingMessage('Loading shopping list...');
    setIsLoading(true);
    setError(null);

    try {
      const response = await getShoppingList({ listId });
      if (response.shoppingList) {
        setSelectedList(response.shoppingList);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shopping list.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteList = async (listId: string) => {
    if (!window.confirm('Delete this shopping list? This cannot be undone.')) {
      return;
    }

    setLoadingMessage('Deleting shopping list...');
    setIsLoading(true);
    setError(null);

    try {
      await deleteShoppingList({ listId });
      setLists((prev) => prev.filter((list) => list.listId !== listId));
      if (selectedList?.listId === listId) {
        setSelectedList(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete shopping list.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleItem = async (itemId: string, isChecked: boolean) => {
    if (!selectedList) {
      return;
    }

    setLoadingMessage('Updating list item...');
    setIsLoading(true);
    setError(null);

    try {
      const response = await checkShoppingListItem({
        listId: selectedList.listId,
        itemId,
        isChecked,
      });

      setSelectedList((prev) => {
        if (!prev) {
          return prev;
        }

        const updatedItems = prev.items.map((item) =>
          item.itemId === itemId
            ? {
                ...item,
                isChecked: response.item?.isChecked ?? isChecked,
              }
            : item
        );

        const checkedCount = updatedItems.filter((item) => item.isChecked).length;
        setLists((current) =>
          current.map((list) =>
            list.listId === prev.listId
              ? {
                  ...list,
                  checkedCount,
                }
              : list
          )
        );

        return {
          ...prev,
          items: updatedItems,
        };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update list item.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadReceiptForList = (listId: string) => {
    // Navigate to upload page with list context for pairing
    navigate('/upload', { state: { pairWithListId: listId } });
  };

  const handleUnpairReceipt = async (listId: string) => {
    if (!window.confirm('Remove the receipt association from this shopping list?')) {
      return;
    }

    setLoadingMessage('Removing receipt association...');
    setIsLoading(true);
    setError(null);

    try {
      const response = await unpairReceiptFromShoppingList({ listId });
      // Update the list in state
      setLists((prev) =>
        prev.map((list) =>
          list.listId === listId
            ? { ...list, pairedReceiptId: undefined }
            : list
        )
      );
      if (selectedList?.listId === listId && response.shoppingList) {
        setSelectedList(response.shoppingList);
      }
      setSuccess('Receipt association removed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove receipt association.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!selectedList || !newItemDescription.trim()) {
      return;
    }

    setLoadingMessage('Adding item...');
    setIsLoading(true);
    setError(null);

    try {
      const item = create(ShoppingListItemInputSchema, {
        description: newItemDescription.trim(),
        quantity: newItemQuantity,
        category: newItemCategory || undefined,
      });

      const response = await addShoppingListItem({
        listId: selectedList.listId,
        item,
      });

      if (response.item) {
        setSelectedList((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            items: [...prev.items, response.item as ShoppingListItem],
          };
        });

        // Update the summary count
        setLists((current) =>
          current.map((list) =>
            list.listId === selectedList.listId
              ? { ...list, itemCount: list.itemCount + 1 }
              : list
          )
        );
      }

      setNewItemDescription('');
      setNewItemQuantity(1);
      setNewItemCategory('');
      setShowAddItem(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add item.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!selectedList) {
      return;
    }

    setLoadingMessage('Removing item...');
    setIsLoading(true);
    setError(null);

    try {
      await removeShoppingListItem({
        listId: selectedList.listId,
        itemId,
      });

      const removedItem = selectedList.items.find((i) => i.itemId === itemId);

      setSelectedList((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.filter((item) => item.itemId !== itemId),
        };
      });

      // Update the summary counts
      setLists((current) =>
        current.map((list) =>
          list.listId === selectedList.listId
            ? {
                ...list,
                itemCount: list.itemCount - 1,
                checkedCount: removedItem?.isChecked
                  ? list.checkedCount - 1
                  : list.checkedCount,
              }
            : list
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove item.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartEdit = (item: ShoppingListItem) => {
    setEditingItemId(item.itemId);
    setEditItemDescription(item.customDescription || item.description);
    setEditItemQuantity(item.quantity || 1);
    setEditItemCategory(item.category || '');
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditItemDescription('');
    setEditItemQuantity(1);
    setEditItemCategory('');
  };

  const handleSaveEdit = async () => {
    if (!selectedList || !editingItemId || !editItemDescription.trim()) {
      return;
    }

    setLoadingMessage('Updating item...');
    setIsLoading(true);
    setError(null);

    try {
      await updateShoppingListItem({
        listId: selectedList.listId,
        itemId: editingItemId,
        customDescription: editItemDescription.trim(),
        quantity: editItemQuantity,
        category: editItemCategory || undefined,
      });

      setSelectedList((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.itemId === editingItemId
              ? { ...item, customDescription: editItemDescription.trim(), quantity: editItemQuantity, category: editItemCategory || undefined }
              : item
          ),
        };
      });

      handleCancelEdit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update item.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartListEdit = (list: ShoppingListSummary) => {
    setEditingListId(list.listId);
    setEditListName(list.name || '');
    setEditListDate(list.date || '');
  };

  const handleCancelListEdit = () => {
    setEditingListId(null);
    setEditListName('');
    setEditListDate('');
  };

  const handleSaveListEdit = async () => {
    if (!editingListId) {
      return;
    }

    setLoadingMessage('Updating list...');
    setIsLoading(true);
    setError(null);

    try {
      const response = await updateShoppingList({
        listId: editingListId,
        name: editListName.trim() || undefined,
        date: editListDate.trim() || undefined,
      });

      if (response.shoppingList) {
        // Update the lists state
        setLists((prev) =>
          prev.map((list) =>
            list.listId === editingListId
              ? {
                  ...list,
                  name: response.shoppingList?.name || list.name,
                  date: response.shoppingList?.date || list.date,
                }
              : list
          )
        );

        // Update selectedList if it's the one being edited
        if (selectedList?.listId === editingListId) {
          setSelectedList(response.shoppingList);
        }
      }

      handleCancelListEdit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update list.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusChip = (list: ShoppingListSummary) => {
    if (list.status === ShoppingListStatus.COMPLETE) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Complete"
          size="small"
          color="success"
          variant="filled"
        />
      );
    }
    return (
      <Chip
        label={`${list.checkedCount}/${list.itemCount} checked`}
        size="small"
        color={list.checkedCount === list.itemCount ? 'success' : 'default'}
        variant="outlined"
      />
    );
  };

  // If viewing a completed list
  if (viewingCompletedList && selectedList) {
    return (
      <Box key="completed-list-view" sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', overflowX: 'hidden' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/lists/completed')}
              sx={{ minWidth: 'auto' }}
            >
              Back to Completed Lists
            </Button>
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {listDisplayName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {selectedList.store || 'Unknown store'} • {selectedList.date || 'No date'}
            {selectedList.pairedReceiptId && ' • Completed'}
          </Typography>
        </Box>

        <ErrorAlert message={error} onDismiss={() => setError(null)} />

        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Items ({selectedList.items.length})
            </Typography>
            {activeCategories.map((category) => (
              <Box key={category} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getCategoryIcon(category)}
                    <Typography variant="subtitle2" fontWeight={600}>
                      {category}
                    </Typography>
                    <Chip label={itemsByCategory[category]?.length ?? 0} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
                  </Stack>
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 200 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: 50 }}>Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(itemsByCategory[category] ?? []).map((item) => (
                        <TableRow key={item.itemId}>
                          <TableCell>
                            {item.customDescription || item.description}
                          </TableCell>
                          <TableCell align="center">{item.quantity || 1}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}

            {selectedList.pairedReceiptId && (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<ReceiptIcon />}
                  onClick={() => navigate(`/receipt/${selectedList.pairedReceiptId}`)}
                >
                  View Linked Receipt
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        <LoadingBackdrop open={isLoading} message={loadingMessage} />
      </Box>
    );
  }

  // If we have receipt data, show the list creation view
  if (receipt) {
    return (
      <Box sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', overflowX: 'hidden' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ minWidth: 'auto' }}
            >
              Back
            </Button>
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Create Shopping List
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create a shopping list from {receipt.store || 'receipt'} ({receipt.date || 'no date'})
          </Typography>
        </Box>

        <ErrorAlert message={error} onDismiss={() => setError(null)} />
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  List Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Name your list to keep it easy to find later.
                </Typography>
              </Box>
              <TextField
                label="List name"
                placeholder="Weekly shopping run"
                value={listName}
                onChange={(event) => setListName(event.target.value)}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleCreateList}
                disabled={receipt.items.length === 0 || isLoading}
                startIcon={<ShoppingCartIcon />}
              >
                Create Shopping List
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Items from Receipt */}
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Items from Receipt ({receipt.items.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 200 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                    <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, width: 50 }}>Qty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipt.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {item.customDescription || item.description}
                      </TableCell>
                      <TableCell align="center">{item.quantity || 1}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {selectedList && (
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {listDisplayName}
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddItem(!showAddItem)}
                  variant={showAddItem ? 'contained' : 'outlined'}
                >
                  Add Item
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Track your progress by checking items as you shop.
              </Typography>

              {showAddItem && (
                <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
                      <Autocomplete
                        freeSolo
                        disablePortal
                        options={itemDescriptionOptions}
                        fullWidth
                        value={newItemDescription}
                        onChange={(_, newValue) => setNewItemDescription(newValue?.toString() ?? '')}
                        onInputChange={(_, newValue, reason) => { if (reason === 'input') setNewItemDescription(newValue); }}
                        sx={{ flex: 1 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Item description"
                            size="small"
                            fullWidth
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && newItemDescription.trim()) {
                                handleAddItem();
                              }
                            }}
                          />
                        )}
                      />
                      <QuantityInput
                        value={newItemQuantity}
                        onChange={setNewItemQuantity}
                        label="Qty"
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
                      <Autocomplete
                        freeSolo
                        options={DEFAULT_CATEGORIES as unknown as string[]}
                        value={newItemCategory}
                        onChange={(_, newValue) => setNewItemCategory(newValue?.toString() ?? '')}
                        sx={{ flex: 1, minWidth: 150 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category (optional)"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          onClick={handleAddItem}
                          disabled={!newItemDescription.trim() || isLoading}
                          size="small"
                        >
                          Add
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowAddItem(false);
                            setNewItemDescription('');
                            setNewItemQuantity(1);
                            setNewItemCategory('');
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sortedSelectedItems.map((item) => item.itemId)}
                  strategy={verticalListSortingStrategy}
                >
                  {activeCategories.map((category) => (
                    <DroppableCategory
                      key={category}
                      category={category}
                      isExpanded={expandedCategories[category] ?? true}
                      onToggle={() => toggleCategory(category)}
                      itemCount={itemsByCategory[category]?.length ?? 0}
                      isOver={dragOverCategory === category}
                    >
                      {/* Mobile card view */}
                      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                        {(itemsByCategory[category] ?? []).map((item) => (
                          <SortableCard key={item.itemId} id={item.itemId}>
                            {editingItemId === item.itemId && !isDesktopViewport ? (
                              <Stack spacing={1.5}>
                                <Autocomplete
                                  freeSolo
                                  options={itemDescriptionOptions}
                                  value={editItemDescription}
                                  fullWidth
                                  onChange={(_, newValue) => setEditItemDescription(newValue?.toString() ?? '')}
                                  onInputChange={(_, newValue, reason) => { if (reason === 'input') setEditItemDescription(newValue); }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      fullWidth
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveEdit();
                                        if (e.key === 'Escape') handleCancelEdit();
                                      }}
                                    />
                                  )}
                                />
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">Qty:</Typography>
                                    <QuantityInput
                                      value={editItemQuantity}
                                      onChange={setEditItemQuantity}
                                    />
                                  </Box>
                                </Box>
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  options={DEFAULT_CATEGORIES as unknown as string[]}
                                  value={editItemCategory}
                                  onChange={(_, newValue) => setEditItemCategory(newValue?.toString() ?? '')}
                                  renderInput={(params) => (
                                    <TextField {...params} placeholder="Category" size="small" />
                                  )}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
                                  <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={handleCancelEdit}>
                                    Cancel
                                  </Button>
                                  <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEdit} disabled={!editItemDescription.trim()}>
                                    Save
                                  </Button>
                                </Box>
                              </Stack>
                            ) : (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Checkbox
                                    checked={item.isChecked}
                                    onChange={(event) => handleToggleItem(item.itemId, event.target.checked)}
                                    size="small"
                                    sx={{ p: 0, mt: 0.25 }}
                                  />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        textDecoration: item.isChecked ? 'line-through' : 'none',
                                        color: item.isChecked ? 'text.secondary' : 'text.primary',
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {item.customDescription || item.description}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, ml: 4 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      textDecoration: item.isChecked ? 'line-through' : 'none',
                                      color: 'text.secondary',
                                    }}
                                  >
                                    Qty: {item.quantity || 1}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton size="small" color="primary" onClick={() => handleStartEdit(item)} sx={{ p: 0.5 }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.itemId)} sx={{ p: 0.5 }}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </SortableCard>
                        ))}
                      </Box>

                      {/* Desktop table view */}
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', display: { xs: 'none', sm: 'block' } }}>
                        <Table size="small" sx={{ minWidth: 320 }}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                              <TableCell sx={{ fontWeight: 600, width: 32, px: 0.5 }}></TableCell>
                              <TableCell sx={{ fontWeight: 600, width: 40, px: 0.5 }}>Done</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, width: 50, px: 0.5 }}>Qty</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, width: 70, px: 0.5 }}></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(itemsByCategory[category] ?? []).map((item) => (
                              <SortableRow key={item.itemId} id={item.itemId}>
                                {editingItemId === item.itemId && isDesktopViewport ? (
                                  <>
                                    <TableCell>
                                      <Checkbox
                                        checked={item.isChecked}
                                        disabled
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Stack spacing={1}>
                                        <Autocomplete
                                          freeSolo
                                          options={itemDescriptionOptions}
                                          value={editItemDescription}
                                          fullWidth
                                          onChange={(_, newValue) => setEditItemDescription(newValue?.toString() ?? '')}
                                          onInputChange={(_, newValue, reason) => { if (reason === 'input') setEditItemDescription(newValue); }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              size="small"
                                              fullWidth
                                              autoFocus
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSaveEdit();
                                                if (e.key === 'Escape') handleCancelEdit();
                                              }}
                                            />
                                          )}
                                        />
                                        <Autocomplete
                                          freeSolo
                                          size="small"
                                          options={DEFAULT_CATEGORIES as unknown as string[]}
                                          value={editItemCategory}
                                          onChange={(_, newValue) => setEditItemCategory(newValue?.toString() ?? '')}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              placeholder="Category"
                                              size="small"
                                            />
                                          )}
                                        />
                                      </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                      <QuantityInput
                                        value={editItemQuantity}
                                        onChange={setEditItemQuantity}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Stack direction="row" spacing={0.5} justifyContent="center">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={handleSaveEdit}
                                          disabled={!editItemDescription.trim()}
                                        >
                                          <SaveIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={handleCancelEdit}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                  </>
                                ) : (
                                  <>
                                    <TableCell>
                                      <Checkbox
                                        checked={item.isChecked}
                                        onChange={(event) => handleToggleItem(item.itemId, event.target.checked)}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        textDecoration: item.isChecked ? 'line-through' : 'none',
                                        color: item.isChecked ? 'text.secondary' : 'text.primary',
                                      }}
                                    >
                                      {item.customDescription || item.description}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        textDecoration: item.isChecked ? 'line-through' : 'none',
                                        color: item.isChecked ? 'text.secondary' : 'text.primary',
                                      }}
                                    >
                                      {item.quantity || 1}
                                    </TableCell>
                                    <TableCell align="center">
                                      <Stack direction="row" spacing={0.5} justifyContent="center">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleStartEdit(item)}
                                          sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleRemoveItem(item.itemId)}
                                          sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    </TableCell>
                                  </>
                                )}
                              </SortableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </DroppableCategory>
                  ))}
                </SortableContext>
                <DragOverlay>
                  {activeDragItem && (
                    <Paper elevation={3} sx={{ p: 1, opacity: 0.9 }}>
                      <Typography variant="body2">
                        {activeDragItem.customDescription || activeDragItem.description}
                      </Typography>
                    </Paper>
                  )}
                </DragOverlay>
              </DndContext>
            </CardContent>
          </Card>
        )}

        <LoadingBackdrop open={isLoading} message={loadingMessage} />
      </Box>
    );
  }

  // If we have receipt summary, show loading/error state while creating list
  if (receiptSummary) {
    return (
      <Box sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', overflowX: 'hidden' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Creating Shopping List
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Creating a shopping list from {receiptSummary.store || 'receipt'} ({receiptSummary.date || 'no date'})
          </Typography>
        </Box>

        <ErrorAlert message={error} onDismiss={() => setError(null)} />

        {error && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/receipts')}
            >
              Back to Receipts
            </Button>
          </Box>
        )}

        <LoadingBackdrop open={isLoading} message={loadingMessage} />
      </Box>
    );
  }

  // Default view - no receipt data
  return (
    <Box key="default-view" sx={{ py: 3, px: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', overflowX: 'hidden' }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Shopping Lists
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage your shopping lists
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm(true)}
        >
          New List
        </Button>
      </Stack>

      <ErrorAlert message={error} onDismiss={() => setError(null)} />

      {/* Create New List Form */}
      {showCreateForm && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              New List
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="List name"
                placeholder="Weekly groceries"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                fullWidth
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateEmptyList();
                  if (e.key === 'Escape') {
                    setShowCreateForm(false);
                    setNewListName('');
                    setNewListStore('');
                  }
                }}
              />
              <Autocomplete
                freeSolo
                options={knownStores}
                inputValue={newListStore}
                onInputChange={(_, newInputValue) => setNewListStore(newInputValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Store (optional)"
                    placeholder="e.g. Whole Foods"
                  />
                )}
                fullWidth
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewListName('');
                    setNewListStore('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateEmptyList}
                  disabled={isLoading}
                  startIcon={<ShoppingCartIcon />}
                >
                  Create List
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {lists.length === 0 && !showCreateForm ? (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <CardContent
            sx={{
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <ShoppingCartIcon
              sx={{
                fontSize: 64,
                color: 'text.disabled',
                mb: 2,
              }}
            />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Shopping Lists Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a new list or start from a receipt.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateForm(true)}
              >
                New List
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/receipts')}
              >
                From Receipt
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : lists.length > 0 ? (
        <Stack spacing={2}>
          {lists.map((list) => (
            <Card
              key={list.listId}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    {editingListId === list.listId ? (
                      <Stack spacing={1.5} sx={{ flex: 1, maxWidth: { sm: '60%' } }}>
                        <TextField
                          label="List name"
                          value={editListName}
                          onChange={(e) => setEditListName(e.target.value)}
                          size="small"
                          fullWidth
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveListEdit();
                            if (e.key === 'Escape') handleCancelListEdit();
                          }}
                        />
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            {list.store || 'Unknown store'} •
                          </Typography>
                          <TextField
                            label="Date"
                            value={editListDate}
                            onChange={(e) => setEditListDate(e.target.value)}
                            size="small"
                            placeholder="e.g. 2024-01-15"
                            sx={{ width: 160 }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveListEdit();
                              if (e.key === 'Escape') handleCancelListEdit();
                            }}
                          />
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveListEdit}
                            disabled={isLoading}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleCancelListEdit}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {list.name || 'Untitled List'}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleStartListEdit(list)}
                            sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            title="Edit list name and date"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {list.store || 'Unknown store'} • {list.date || 'No date'}
                        </Typography>
                      </Box>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      {getStatusChip(list)}
                      {list.pairedReceiptId ? (
                        <Chip
                          icon={<ReceiptIcon />}
                          label="Receipt linked"
                          size="small"
                          color="info"
                          variant="outlined"
                          onClick={() => navigate(`/receipt/${list.pairedReceiptId}`)}
                          sx={{ cursor: 'pointer' }}
                        />
                      ) : getUploadForList(list.listId) ? (
                        <Chip
                          icon={<CircularProgress size={14} color="inherit" />}
                          label="Processing..."
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<LinkOffIcon />}
                          label="No receipt"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                      )}
                      <IconButton
                        onClick={() => handleViewList(list.listId)}
                        color="primary"
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      {list.pairedReceiptId ? (
                        <IconButton
                          onClick={() => handleUnpairReceipt(list.listId)}
                          color="warning"
                          size="small"
                          title="Unlink receipt"
                          sx={{
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.warning.main, 0.2),
                            },
                          }}
                        >
                          <LinkOffIcon fontSize="small" />
                        </IconButton>
                      ) : getUploadForList(list.listId) ? (
                        <IconButton
                          disabled
                          color="primary"
                          size="small"
                          title="Upload in progress"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          }}
                        >
                          <CircularProgress size={18} />
                        </IconButton>
                      ) : (
                        <IconButton
                          onClick={() => handleUploadReceiptForList(list.listId)}
                          color="primary"
                          size="small"
                          title="Upload receipt"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                          }}
                        >
                          <UploadIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => handleDeleteList(list.listId)}
                        color="error"
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.error.main, 0.1),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.error.main, 0.2),
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {selectedList?.listId === list.listId && (
                    <>
                      <Divider />
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {listDisplayName}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setShowAddItem(!showAddItem)}
                            variant={showAddItem ? 'contained' : 'outlined'}
                          >
                            Add Item
                          </Button>
                        </Stack>

                        {showAddItem && (
                          <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                            <Stack spacing={2}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
                                <Autocomplete
                                  freeSolo
                                  disablePortal
                                  options={itemDescriptionOptions}
                                  fullWidth
                                  value={newItemDescription}
                                  onChange={(_, newValue) => setNewItemDescription(newValue?.toString() ?? '')}
                                  onInputChange={(_, newValue, reason) => { if (reason === 'input') setNewItemDescription(newValue); }}
                                  sx={{ flex: 1 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Item description"
                                      size="small"
                                      fullWidth
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newItemDescription.trim()) {
                                          handleAddItem();
                                        }
                                      }}
                                    />
                                  )}
                                />
                                <QuantityInput
                                  value={newItemQuantity}
                                  onChange={setNewItemQuantity}
                                  label="Qty"
                                />
                              </Stack>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-end">
                                <Autocomplete
                                  freeSolo
                                  options={DEFAULT_CATEGORIES as unknown as string[]}
                                  value={newItemCategory}
                                  onChange={(_, newValue) => setNewItemCategory(newValue?.toString() ?? '')}
                                  sx={{ flex: 1, minWidth: 150 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Category (optional)"
                                      size="small"
                                      fullWidth
                                    />
                                  )}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Button
                                    variant="contained"
                                    onClick={handleAddItem}
                                    disabled={!newItemDescription.trim() || isLoading}
                                    size="small"
                                  >
                                    Add
                                  </Button>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setShowAddItem(false);
                                      setNewItemDescription('');
                                      setNewItemQuantity(1);
                                      setNewItemCategory('');
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </Stack>
                          </Card>
                        )}

                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragStart={handleDragStart}
                          onDragOver={handleDragOver}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={sortedSelectedItems.map((item) => item.itemId)}
                            strategy={verticalListSortingStrategy}
                          >
                            {activeCategories.map((category) => (
                              <DroppableCategory
                                key={category}
                                category={category}
                                isExpanded={expandedCategories[category] ?? true}
                                onToggle={() => toggleCategory(category)}
                                itemCount={itemsByCategory[category]?.length ?? 0}
                                isOver={dragOverCategory === category}
                              >
                                {/* Mobile card view */}
                                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                  {(itemsByCategory[category] ?? []).map((item) => (
                                    <SortableCard key={item.itemId} id={item.itemId}>
                                      {editingItemId === item.itemId && !isDesktopViewport ? (
                                        <Stack spacing={1.5}>
                                          <Autocomplete
                                            freeSolo
                                            options={itemDescriptionOptions}
                                            value={editItemDescription}
                                            fullWidth
                                            onChange={(_, newValue) => setEditItemDescription(newValue?.toString() ?? '')}
                                            onInputChange={(_, newValue, reason) => { if (reason === 'input') setEditItemDescription(newValue); }}
                                            renderInput={(params) => (
                                              <TextField
                                                {...params}
                                                size="small"
                                                fullWidth
                                                autoFocus
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') handleSaveEdit();
                                                  if (e.key === 'Escape') handleCancelEdit();
                                                }}
                                              />
                                            )}
                                          />
                                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Typography variant="caption" color="text.secondary">Qty:</Typography>
                                              <QuantityInput
                                                value={editItemQuantity}
                                                onChange={setEditItemQuantity}
                                              />
                                            </Box>
                                          </Box>
                                          <Autocomplete
                                            freeSolo
                                            size="small"
                                            options={DEFAULT_CATEGORIES as unknown as string[]}
                                            value={editItemCategory}
                                            onChange={(_, newValue) => setEditItemCategory(newValue?.toString() ?? '')}
                                            renderInput={(params) => (
                                              <TextField {...params} placeholder="Category" size="small" />
                                            )}
                                          />
                                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 0.5 }}>
                                            <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={handleCancelEdit}>
                                              Cancel
                                            </Button>
                                            <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEdit} disabled={!editItemDescription.trim()}>
                                              Save
                                            </Button>
                                          </Box>
                                        </Stack>
                                      ) : (
                                        <Box>
                                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Checkbox
                                              checked={item.isChecked}
                                              onChange={(event) => handleToggleItem(item.itemId, event.target.checked)}
                                              size="small"
                                              sx={{ p: 0, mt: 0.25 }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  textDecoration: item.isChecked ? 'line-through' : 'none',
                                                  color: item.isChecked ? 'text.secondary' : 'text.primary',
                                                  wordBreak: 'break-word',
                                                }}
                                              >
                                                {item.customDescription || item.description}
                                              </Typography>
                                            </Box>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, ml: 4 }}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                textDecoration: item.isChecked ? 'line-through' : 'none',
                                                color: 'text.secondary',
                                              }}
                                            >
                                              Qty: {item.quantity || 1}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                              <IconButton size="small" color="primary" onClick={() => handleStartEdit(item)} sx={{ p: 0.5 }}>
                                                <EditIcon fontSize="small" />
                                              </IconButton>
                                              <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.itemId)} sx={{ p: 0.5 }}>
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Box>
                                        </Box>
                                      )}
                                    </SortableCard>
                                  ))}
                                </Box>

                                {/* Desktop table view */}
                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', display: { xs: 'none', sm: 'block' } }}>
                                  <Table size="small" sx={{ minWidth: 320 }}>
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                                        <TableCell sx={{ fontWeight: 600, width: 32, px: 0.5 }}></TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 40, px: 0.5 }}>Done</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, width: 50, px: 0.5 }}>Qty</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600, width: 70, px: 0.5 }}></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(itemsByCategory[category] ?? []).map((item) => (
                                        <SortableRow key={item.itemId} id={item.itemId}>
                                          {editingItemId === item.itemId && isDesktopViewport ? (
                                            <>
                                              <TableCell>
                                                <Checkbox
                                                  checked={item.isChecked}
                                                  disabled
                                                  size="small"
                                                />
                                              </TableCell>
                                              <TableCell>
                                                <Stack spacing={1}>
                                                  <Autocomplete
                                                    freeSolo
                                                    options={itemDescriptionOptions}
                                                    value={editItemDescription}
                                                    fullWidth
                                                    onChange={(_, newValue) => setEditItemDescription(newValue?.toString() ?? '')}
                                                    onInputChange={(_, newValue, reason) => { if (reason === 'input') setEditItemDescription(newValue); }}
                                                    renderInput={(params) => (
                                                      <TextField
                                                        {...params}
                                                        size="small"
                                                        fullWidth
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                          if (e.key === 'Enter') handleSaveEdit();
                                                          if (e.key === 'Escape') handleCancelEdit();
                                                        }}
                                                      />
                                                    )}
                                                  />
                                                  <Autocomplete
                                                    freeSolo
                                                    size="small"
                                                    options={DEFAULT_CATEGORIES as unknown as string[]}
                                                    value={editItemCategory}
                                                    onChange={(_, newValue) => setEditItemCategory(newValue?.toString() ?? '')}
                                                    renderInput={(params) => (
                                                      <TextField
                                                        {...params}
                                                        placeholder="Category"
                                                        size="small"
                                                      />
                                                    )}
                                                  />
                                                </Stack>
                                              </TableCell>
                                              <TableCell align="center">
                                                <QuantityInput
                                                  value={editItemQuantity}
                                                  onChange={setEditItemQuantity}
                                                />
                                              </TableCell>
                                              <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={handleSaveEdit}
                                                    disabled={!editItemDescription.trim()}
                                                  >
                                                    <SaveIcon fontSize="small" />
                                                  </IconButton>
                                                  <IconButton
                                                    size="small"
                                                    onClick={handleCancelEdit}
                                                  >
                                                    <CloseIcon fontSize="small" />
                                                  </IconButton>
                                                </Stack>
                                              </TableCell>
                                            </>
                                          ) : (
                                            <>
                                              <TableCell>
                                                <Checkbox
                                                  checked={item.isChecked}
                                                  onChange={(event) => handleToggleItem(item.itemId, event.target.checked)}
                                                  size="small"
                                                />
                                              </TableCell>
                                              <TableCell
                                                sx={{
                                                  textDecoration: item.isChecked ? 'line-through' : 'none',
                                                  color: item.isChecked ? 'text.secondary' : 'text.primary',
                                                }}
                                              >
                                                {item.customDescription || item.description}
                                              </TableCell>
                                              <TableCell
                                                align="center"
                                                sx={{
                                                  textDecoration: item.isChecked ? 'line-through' : 'none',
                                                  color: item.isChecked ? 'text.secondary' : 'text.primary',
                                                }}
                                              >
                                                {item.quantity || 1}
                                              </TableCell>
                                              <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleStartEdit(item)}
                                                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                                                  >
                                                    <EditIcon fontSize="small" />
                                                  </IconButton>
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemoveItem(item.itemId)}
                                                    sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </Stack>
                                              </TableCell>
                                            </>
                                          )}
                                        </SortableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </DroppableCategory>
                            ))}
                          </SortableContext>
                          <DragOverlay>
                            {activeDragItem && (
                              <Paper elevation={3} sx={{ p: 1, opacity: 0.9 }}>
                                <Typography variant="body2">
                                  {activeDragItem.customDescription || activeDragItem.description}
                                </Typography>
                              </Paper>
                            )}
                          </DragOverlay>
                        </DndContext>
                      </Box>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : null}

      <LoadingBackdrop open={isLoading} message={loadingMessage} />
    </Box>
  );
}
